import { create } from "zustand";
import { AppRecordJK, AppRecordStatusJK, RequestApi, RouteApi, RouteUrl, StatusMsg } from "utils";
import { AppLog } from "services";
import { Service } from "utils/services";
import { getSubComments, onPostReaction, postReactionComment, postSubmitComment } from "api-function/postContentApi";
import { PostReaction } from "models/postReaction";
import { MessageType } from "utils/enum/message_type";
import socket from "services/socket";
import { NavigateFunction } from "react-router-dom";

interface FollowingState {
  users: any[];
  usersAppRecord: AppRecordJK;
  selectedUserId: string;
  posts: any[];
  postsAppRecord: AppRecordJK;
  excludedPostIds: Set<string>;
  currentPost: any;
  comments: any[];
  commentAppRecord: AppRecordJK;
  showComments: boolean;

  initializePage: (userId: string | null, tag: string) => Promise<void>;
  fetchFollowingUsers: (params?: { isRefresh?: boolean }) => Promise<void>;
  fetchFollowingPosts: (params?: { isRefresh?: boolean }) => Promise<void>;
  setCurrentPost: (index: number) => Promise<void>;
  onClickedLike: (post: any) => Promise<void>;
  onClickedShare: (post: any) => Promise<void>;
  onClickedFavorite: (post: any) => Promise<void>;
  onToggleShowComment: () => Promise<void>;
  fetchComments: (isRefresh: boolean) => Promise<void>;
  fetchSubComments: (commentId: string, nextPageUrl?: string) => Promise<void>;
  requestReactionComment: (comment: any) => Promise<void>;
  onUpdateReactionLike: (comments: any[], targetId: string, newSubComment: any) => any[];
  requestReplyComment: ({
    replyId,
    replyText,
    headerId,
    commentId,
    commentUrl,
  }: {
    replyId: string;
    replyText: string;
    headerId: string;
    commentId: string;
    commentUrl: string;
  }) => Promise<void>;
  requestSubmitComment: (sticker: any, textComment: string) => Promise<void>;
  onAddOrUpdateSubComment(comments: any[], targetId: string, headerId: string, newSubComment: any, addNew: boolean): any[];
  commentSocket: () => void;
  onClickedUser: (navigate: NavigateFunction, user: any) => void;

  // * ----- left Side ----- * //
  tag: string;
  topTenFollowers: any[];
  topTenReachs: any[];
  fetchTopTenFollower: () => Promise<void>;
  fetchTopTenReach: () => Promise<void>;
  topTenFollowerLoading: boolean;
  topTenReachLoading: boolean;
}

const useFollowingStore = create<FollowingState>((set, get) => ({
  users: [],
  usersAppRecord: new AppRecordJK(),
  selectedUserId: "",
  posts: [],
  currentPost: null,
  postsAppRecord: new AppRecordJK(),
  excludedPostIds: new Set<string>(),
  currentPostIndex: 0,
  comments: [],
  commentAppRecord: new AppRecordJK(),
  showComments: false,
  tag: "following",

  initializePage: async (userId: string | null, tag: string) => {
    try {
      const tmpTag = tag;
      const tmpUserId = tmpTag === "following" ? userId ?? "" : "";

      set({
        users: [],
        usersAppRecord: new AppRecordJK(),
        selectedUserId: tmpUserId,
        posts: [],
        postsAppRecord: new AppRecordJK({ loadingStatus: AppRecordStatusJK.loading }),
        excludedPostIds: new Set<string>(),

        // * ----- left Side ----- * //
        tag: tmpTag,
        topTenFollowers: [],
        topTenFollowerLoading: true,
        topTenReachs: [],
        topTenReachLoading: true,
      });

      if (tmpTag === "following") {
        await useFollowingStore
          .getState()
          .fetchFollowingUsers({ isRefresh: true })
          .then(async (_) => {
            await useFollowingStore.getState().fetchFollowingPosts({ isRefresh: true });
            await useFollowingStore.getState().commentSocket();
          });
      } else {
        await useFollowingStore.getState().fetchTopTenFollower();
        await useFollowingStore.getState().fetchTopTenReach();

        await useFollowingStore.getState().fetchFollowingPosts({ isRefresh: true });
        await useFollowingStore.getState().commentSocket();
      }
    } catch (error) {
      AppLog.show({
        message: `[initializePage]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchFollowingUsers: async ({
    isRefresh = false,
  }: Partial<{
    isRefresh: boolean;
  }> = {}) => {
    const { users, usersAppRecord } = get();

    let tmpUsers = [...users];
    let tmpUsersAppRecord = new AppRecordJK({
      ...usersAppRecord,
    });

    try {
      let url: string = "";
      url = RouteApi.getFollowingUser;
      url += `token=${Service.token}`;
      url += "&flag=post";

      if (isRefresh) {
        tmpUsersAppRecord.loadingStatus = AppRecordStatusJK.loading;
        tmpUsers = [];
        set({
          users: tmpUsers,
          usersAppRecord: tmpUsersAppRecord,
        });
      } else {
        if (tmpUsersAppRecord.canLoadMore()) {
          tmpUsersAppRecord.loadingStatus = AppRecordStatusJK.loadingMore;
          set({ usersAppRecord: tmpUsersAppRecord });
          url = tmpUsersAppRecord.nextPageUrl!;
        } else {
          return;
        }
      }
      const responses = await RequestApi(url);
      if (responses.status === "success") {
        const followingUserData = responses.following_users;

        if (followingUserData.next_page_url) {
          tmpUsersAppRecord.loadingStatus = AppRecordStatusJK.loaded;
        } else {
          tmpUsersAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        }

        tmpUsersAppRecord.nextPageUrl = followingUserData.next_page_url;

        set({
          usersAppRecord: tmpUsersAppRecord,
          users: [...tmpUsers, ...followingUserData.data],
        });
      } else {
        tmpUsersAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({ usersAppRecord: tmpUsersAppRecord });
      }
    } catch (error) {
      tmpUsersAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
      set({ usersAppRecord: tmpUsersAppRecord });

      AppLog.show({
        message: `[fetchFollowingUsers]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchFollowingPosts: async ({
    isRefresh = false,
  }: Partial<{
    isRefresh: boolean;
  }> = {}) => {
    const { tag, posts, postsAppRecord, excludedPostIds, selectedUserId } = get();

    let tmpPosts = [...posts];
    let tmpExcludedPostIds = new Set(excludedPostIds);
    let tmpPostsAppRecord = new AppRecordJK({
      ...postsAppRecord,
    });

    try {
      let url: string = RouteApi.getPostWeb;
      url += `token=${Service.token}`;
      if (isRefresh) {
        tmpPostsAppRecord.loadingStatus = AppRecordStatusJK.loading;
        tmpPosts = [];
        tmpExcludedPostIds = new Set<string>();
        set({
          posts: tmpPosts,
          postsAppRecord: tmpPostsAppRecord,
        });
      } else {
        if (tmpPostsAppRecord.canLoadMore()) {
          tmpPostsAppRecord.loadingStatus = AppRecordStatusJK.loadingMore;
          set({ postsAppRecord: tmpPostsAppRecord });
        } else {
          return;
        }
      }

      const excludedPostIdsArray: string[] = [...tmpExcludedPostIds];

      const data = {
        user_id: selectedUserId,
        post_ids: excludedPostIdsArray,
        tag: tag,
      };

      const responses = await RequestApi(url, { data: data });
      if (responses.status === "success") {
        const newPosts: any[] = responses.posts;
        if (newPosts && newPosts.length > 0) {
          tmpPostsAppRecord.loadingStatus = AppRecordStatusJK.loaded;
        } else {
          tmpPostsAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        }

        const filteredUsers = newPosts.filter((post) => !tmpExcludedPostIds.has(`${post.id}`));

        filteredUsers.forEach((user) => tmpExcludedPostIds.add(`${user.id}`));

        set({
          postsAppRecord: tmpPostsAppRecord,
          posts: [...tmpPosts, ...newPosts],
          excludedPostIds: tmpExcludedPostIds,
        });
      } else {
        tmpPostsAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({ postsAppRecord: tmpPostsAppRecord });
      }
    } catch (error) {
      tmpPostsAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
      set({ postsAppRecord: tmpPostsAppRecord });

      AppLog.show({
        message: `[fetchFollowingPosts]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  setCurrentPost: async (index: number) => {
    try {
      const { posts, showComments } = get();
      if (posts.length === 0) return;
      set({ currentPost: posts[index] });
      if (showComments) {
        await useFollowingStore.getState().fetchComments(true);
      } else {
        set({ commentAppRecord: new AppRecordJK(), comments: [] });
      }
    } catch (error) {
      AppLog.show({
        message: `[setCurrentPost]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onClickedLike: async (post: any) => {
    try {
      const { posts } = get();
      if (Service.userAuth) {
        const attachment: any = post?.post_attachments[0];
        const isLike: boolean = !Boolean(Service.toInteger(post?.is_reaction_like));

        const responses = await onPostReaction({
          content: post,
          reaction: PostReaction.like,
          isLike: isLike,
          isFavorite: false,
          attachment,
        });

        if (responses.status === "success") {
          let tmpPost = structuredClone(post);
          tmpPost.is_reaction_like = isLike ? "1" : "0";
          const numberLike = isLike ? 1 : -1;
          tmpPost.reaction_like_count = Service.toInteger(tmpPost.reaction_like_count) + numberLike;

          const editIndex = posts.findIndex((post) => post.provider_post_id === tmpPost.provider_post_id);
          if (editIndex !== -1) {
            set((state) => {
              const updatedPosts = [...state.posts];
              updatedPosts[editIndex] = tmpPost;
              return { posts: updatedPosts };
            });
          }
        } else {
        }
      }
    } catch (error) {
      AppLog.show({
        message: `[onClickedLike]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onClickedShare: async (post: any) => {
    try {
      const { posts } = get();
      const tmpPost = { ...post };

      const editIndex = posts.findIndex((post) => post.provider_post_id === tmpPost.provider_post_id);
      if (editIndex !== -1) {
        set((state) => {
          const updatedPosts = [...state.posts];
          updatedPosts[editIndex] = tmpPost;
          return { posts: updatedPosts };
        });
      }
    } catch (error) {
      AppLog.show({
        message: `[onClickedShare]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onClickedFavorite: async (post: any) => {
    try {
      const { posts } = get();
      if (Service.userAuth) {
        const attachment: any = post?.post_attachments[0];
        const isFavorite: boolean = !Boolean(Service.toInteger(post?.is_reaction_favorite));

        const responses = await onPostReaction({
          content: post,
          reaction: PostReaction.favorite,
          isLike: false,
          isFavorite: isFavorite,
          attachment,
        });

        if (responses.status === "success") {
          let tmpPost = structuredClone(post);
          tmpPost.is_reaction_favorite = isFavorite ? "1" : "0";
          const numberFavorite = isFavorite ? 1 : -1;
          tmpPost.reaction_favorite_count = Service.toInteger(tmpPost.reaction_favorite_count) + numberFavorite;

          const editIndex = posts.findIndex((post) => post.provider_post_id === tmpPost.provider_post_id);
          if (editIndex !== -1) {
            set((state) => {
              const updatedPosts = [...state.posts];
              updatedPosts[editIndex] = tmpPost;
              return { posts: updatedPosts };
            });
          }
        } else {
        }
      }
    } catch (error) {
      AppLog.show({
        message: `[onClickedFavorite]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onToggleShowComment: async () => {
    try {
      const { showComments, comments } = get();
      let tmpShowComments = showComments;
      if (tmpShowComments) {
        tmpShowComments = !tmpShowComments;
      } else {
        tmpShowComments = !tmpShowComments;
        if (comments.length === 0) await useFollowingStore.getState().fetchComments(true);
      }
      set({ showComments: tmpShowComments });
    } catch (error) {
      AppLog.show({
        message: `[onToggleShowComment]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchComments: async (isRefresh: boolean) => {
    const { comments, commentAppRecord, currentPost } = get();
    if (!currentPost) return;
    let tmpComments = [...comments];
    let tmpCommentAppRecord = new AppRecordJK({ ...commentAppRecord });
    try {
      let url: string = "";
      url = RouteApi.getComment;
      url += `token=${Service.token}`;
      url += `&post_id=${currentPost?.provider_post_id}`;
      url += `&attachment_id=${currentPost?.post_attachments[0].attachment_id}`;

      if (isRefresh) {
        tmpCommentAppRecord.loadingStatus = AppRecordStatusJK.loading;

        tmpComments = [];
        set({
          commentAppRecord: tmpCommentAppRecord,
          comments: tmpComments,
        });
      } else {
        if (tmpCommentAppRecord.nextPageUrl) {
          tmpCommentAppRecord.loadingStatus = AppRecordStatusJK.loadingMore;
          set({ commentAppRecord: tmpCommentAppRecord });
          url = tmpCommentAppRecord.nextPageUrl;
        } else {
          return;
        }
      }

      const responses = await RequestApi(url);
      if (responses.status === "success") {
        const resData = responses.comments;

        if (resData.next_page_url) {
          tmpCommentAppRecord.loadingStatus = AppRecordStatusJK.loaded;
          tmpCommentAppRecord.nextPageUrl = resData.next_page_url;
        } else {
          tmpCommentAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
          tmpCommentAppRecord.nextPageUrl = resData.next_page_url;
        }

        set({
          commentAppRecord: tmpCommentAppRecord,
          comments: [...tmpComments, ...resData.data],
        });
      } else {
        tmpCommentAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({ commentAppRecord: tmpCommentAppRecord });
      }
    } catch (error) {
      tmpCommentAppRecord.loadingStatus = AppRecordStatusJK.loaded;
      set({ commentAppRecord: tmpCommentAppRecord });

      AppLog.show({
        message: `[fetchComments]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchSubComments: async (commentId: string, nextPageUrl?: string) => {
    const { comments, currentPost } = get();

    if (!currentPost) return;

    try {
      const result = await getSubComments({
        postId: currentPost?.provider_post_id,
        attachmentId: currentPost?.post_attachments[0]["attachment_id"],
        replyCommentId: commentId,
        nextPageUrl: nextPageUrl,
      });

      if (result.status === "success") {
        const fetchedSubComments = {
          data: [...result["comments"]["data"]],
          next_page_url: result["comments"]["next_page_url"],
        };

        const updateCommentWithSubComments = (comments: any, targetId: string, subComments: any) => {
          return comments.map((comment: any) => {
            if (comment.id.toString() === targetId) {
              const existingSubComments = comment.sub_comments?.data || [];
              const mergedSubComments = Array.from(new Map([...existingSubComments, ...subComments.data].map((sc) => [sc.id, sc])).values());

              const updatedSubComments = {
                ...subComments,
                data: mergedSubComments,
              };

              return { ...comment, sub_comments: updatedSubComments };
            }

            if (comment.sub_comments?.data?.length) {
              return {
                ...comment,
                sub_comments: {
                  ...comment.sub_comments,
                  data: updateCommentWithSubComments(comment.sub_comments.data, targetId, subComments),
                },
              };
            }

            return comment;
          });
        };
        const updatedComments = updateCommentWithSubComments(comments, commentId, fetchedSubComments);
        set({ comments: [...updatedComments] });
      } else {
        AppLog.show({
          message: `Failed to fetch sub-comments: ${result["msg"]}`,
        });
      }
    } catch (error) {
      AppLog.show({
        message: `[fetchSubComments]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  requestReactionComment: async ({ comment }: { comment: any }) => {
    const { comments } = get();
    try {
      let tmpComment = comment;
      const isLike = tmpComment["is_like"]?.toString() === "0" ? "1" : "0";
      const data = {
        comment_id: tmpComment["comment_id"]?.toString(),
        is_like: isLike,
        key_remove_id: await Service.getSynchID(),
      };

      const updatedComments = get().onUpdateReactionLike(comments, tmpComment["comment_id"].toString(), data);
      set({ comments: [...updatedComments] });

      const result = await postReactionComment({ data: data });
      if (result.status === StatusMsg.success.value) {
      } else {
        AppLog.show({
          message: `Error ${result["msg"]}`,
          status: StatusMsg.success,
        });
      }
    } catch (err) {
      AppLog.show({
        message: `Error Catch requestReactionComment ${err}`,
        status: StatusMsg.error,
      });
    }
  },

  onUpdateReactionLike: (comments: any[], targetId: string, newSubComment: any) => {
    return comments.map((comment: any) => {
      if (comment.comment_id.toString() === targetId) {
        return {
          ...comment,
          is_like: newSubComment["is_like"],
          key_remove_id: newSubComment["key_remove_id"],
        };
      }
      if (comment.sub_comments?.data?.length) {
        return {
          ...comment,
          sub_comments: {
            ...comment.sub_comments,
            data: get().onUpdateReactionLike(comment.sub_comments.data, targetId, newSubComment),
          },
        };
      }
      return comment;
    });
  },

  requestReplyComment: async ({
    replyId,
    replyText,
    headerId,
    commentId,
    commentUrl,
  }: {
    replyId: string;
    replyText: string;
    headerId: string;
    commentId: string;
    commentUrl: string;
  }) => {
    const { comments, currentPost } = get();
    if (!currentPost) return;
    try {
      const comment = {
        user_id: currentPost?.user_id,
        reply_comment_id: replyId,
        comment: replyText,
        comment_type: commentUrl === "" ? MessageType.text.value : MessageType.sticker.value,
        comment_url: commentUrl,
        post_id: currentPost?.provider_post_id.toString(),
        attachment_id: currentPost?.post_attachments[0].attachment_id.toString(),
        key_remove_id: await Service.getSynchID(),
      };

      const tmpSubComment = {
        id: 0,
        user_id: Service?.userAuth["id"].toString(),
        comment_id: commentId,
        sender: {
          id: Service?.userAuth["id"].toString(),
          email: Service?.userAuth["email"].toString(),
          username: Service?.userAuth["username"].toString(),
          name: Service?.userAuth["name"].toString(),
          gender: Service?.userAuth["gender"].toString(),
          picture: Service?.userAuth["picture"].toString(),
          thumbnail: Service?.userAuth["thumbnail"].toString(),
        },
        reply_comment_id: comment["reply_comment_id"],
        comment: replyText,
        comment_datetime: new Date().toISOString(), // Convert date to string
        sending: true,
        key_remove_id: comment["key_remove_id"],
      };
      const updatedComments = get().onAddOrUpdateSubComment(comments, replyId, headerId, tmpSubComment, true);
      set({ comments: [...updatedComments] });

      const result = await postSubmitComment({ comment: comment });
      if (result.status === StatusMsg.success.value) {
      } else {
      }
    } catch (err) {
      AppLog.show({
        message: `ERROR CATCH ${err}`,
        status: StatusMsg.error,
      });
    }
  },

  requestSubmitComment: async (sticker: any, textComment: string) => {
    const { currentPost, comments } = get();

    if (!currentPost) return;

    try {
      const commentType = Object.keys(sticker).length === 0 ? MessageType.text.value : MessageType.sticker.value;
      const comment = {
        user_id: currentPost?.user_id,
        reply_comment_id: "",
        comment: textComment,
        comment_type: commentType,
        comment_url: Object.keys(sticker).length === 0 ? "" : sticker.thumbnail_url,
        post_id: currentPost?.provider_post_id.toString(),
        attachment_id: currentPost?.post_attachments[0].attachment_id.toString(),
        key_remove_id: await Service.getSynchID(),
      };
      const tmpComment = {
        id: 0,
        is_sending: true,
        user_id: Service?.userAuth["id"].toString(),
        sender: {
          id: Service?.userAuth["id"].toString(),
          email: Service?.userAuth["email"].toString(),
          username: Service?.userAuth["username"].toString(),
          name: Service?.userAuth["name"].toString(),
          gender: Service?.userAuth["gender"].toString(),
          picture: Service?.userAuth["picture"].toString(),
          thumbnail: Service?.userAuth["thumbnail"].toString(),
        },
        reply_comment_id: comment["reply_comment_id"],
        comment: comment["comment"],
        comment_datetime: new Date().toISOString(),
        sending: true,
        key_remove_id: comment["key_remove_id"],
      };
      let newComments = [...comments];
      newComments.unshift(tmpComment);
      set({ comments: [...newComments] });
      const result = await postSubmitComment({ comment: comment });
      if (result.status === StatusMsg.success.value) {
      } else {
        AppLog.show({
          message: `Error ${result["msg"]}`,
          status: StatusMsg.success,
        });
      }
    } catch (err) {
      AppLog.show({
        message: `Error Catch postSubmitcomment ${err}`,
        status: StatusMsg.error,
      });
    }
  },

  onAddOrUpdateSubComment(comments: any[], targetId: string, headerId: string, newSubComment: any, addNew: boolean): any[] {
    return comments.map((comment: any) => {
      if (comment.id.toString() === targetId) {
        if (!comment.sub_comments || !comment.sub_comments.data) {
          return {
            ...comment,
            sub_comments: {
              data: [newSubComment],
            },
          };
        }
        const tmpSubComment = [...comment.sub_comments.data];
        const index = tmpSubComment.findIndex((item: any) => item["key_remove_id"]?.toString() === headerId);
        if (index !== -1) {
          tmpSubComment[index] = {
            ...tmpSubComment[index],
            ...newSubComment,
          };
          return {
            ...comment,
            sub_comments: {
              ...comment.sub_comments,
              data: tmpSubComment,
            },
          };
        } else {
          return {
            ...comment,
            sub_comments: {
              ...comment.sub_comments,
              data: [newSubComment, ...tmpSubComment],
            },
          };
        }
      }
      if (comment.sub_comments?.data?.length) {
        return {
          ...comment,
          sub_comments: {
            ...comment.sub_comments,
            data: get().onAddOrUpdateSubComment(comment.sub_comments.data, targetId, headerId, newSubComment, addNew),
          },
        };
      }

      return comment;
    });
  },

  commentSocket: () => {
    socket.on("onFlipFlopComment", (result: any) => {
      const { comments, currentPost, showComments } = get();

      if (showComments === false) return;

      console.log("commentSocket ===== ");

      let tmpComments = [...comments];
      let tmpPost = { ...currentPost };
      console.log("====listen type", result);
      const postComments = [...result["post_comments"]];

      if (result["flag"] === "added") {
        postComments.forEach((postComment) => {
          if (`${currentPost?.provider_post_id}` === `${postComment?.post_id}`) {
            const keyRemoveId = postComment["key_remove_id"]?.toString();
            const replyCommentId = postComment["reply_comment_id"]?.toString();
            if (replyCommentId === "0" || replyCommentId === "") {
              const index = tmpComments.findIndex((item: any) => item["key_remove_id"]?.toString() === keyRemoveId);
              if (index !== -1) {
                tmpComments[index] = { ...postComment };
              } else {
                tmpComments = [postComment, ...tmpComments];
              }
              tmpPost.comments.data = [...tmpComments];
              set({ comments: tmpComments, currentPost: tmpPost });
            } else {
              const resposeComment = postComment;
              const updateResponseComment = get().onAddOrUpdateSubComment(tmpComments, replyCommentId, keyRemoveId, resposeComment, false);
              tmpPost.comments.data = [...tmpComments];
              set({ comments: updateResponseComment, currentPost: tmpPost });
            }
          }
        });
      } else if (result["flag"] === "liked") {
        if (result["reaction_user_id"]?.toString() === Service.userAuth["id"].toString()) {
          const updatedComments = get().onUpdateReactionLike(tmpComments, result["comment_id"], postComments[0]);
          tmpComments = updatedComments;
          tmpPost.comments.data = [...tmpComments];
          set({ comments: updatedComments, currentPost: tmpPost });
        } else {
          console.log("No target");
        }
      }
    });
  },

  onClickedUser: async (navigate: NavigateFunction, user: any) => {
    try {
      const { selectedUserId } = get();
      let tmpSelectedUserId = selectedUserId;
      if (tmpSelectedUserId === `${user.id}`) {
        tmpSelectedUserId = "";
        navigate(`${RouteUrl.following}`, {
          replace: true,
        });
      } else {
        tmpSelectedUserId = `${user.id}`;
        navigate(`${RouteUrl.following}?user_id=${tmpSelectedUserId}`, {
          replace: true,
        });
      }
      set({ selectedUserId: tmpSelectedUserId });
      await useFollowingStore.getState().fetchFollowingPosts({ isRefresh: true });
    } catch (error) {
      AppLog.show({
        message: `[onClickedUser]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  // * ----- left Side ----- * //
  topTenFollowers: [],
  topTenFollowerLoading: true,
  topTenReachs: [],
  topTenReachLoading: true,

  fetchTopTenFollower: async () => {
    try {
      let url = RouteApi.getHomeTopTenFollwers;
      url += `token=${Service.token}`;
      const responses = await RequestApi(url);
      if (responses.status === "success") {
        set({ topTenFollowers: responses.users });
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[fetchTopTenFollower]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      set({ topTenFollowerLoading: false });
    }
  },

  fetchTopTenReach: async () => {
    try {
      set({ topTenReachLoading: true });

      let url = RouteApi.getHomeTopTenReachs;
      url += `token=${Service.token}`;
      const responses = await RequestApi(url);
      if (responses.status === "success") {
        set({ topTenReachs: responses.users });
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[fetchTopTenReach]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      set({ topTenReachLoading: false });
    }
  },
}));

export default useFollowingStore;

